import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  ArrowLeft, Check, Minus, Plus, Search, UserPlus, UserX, Loader2, Banknote, QrCode, CreditCard, X,
} from "lucide-react";

const TYPE_LABELS = { dinheiro: "Dinheiro", pix: "PIX", debito: "Débito", credito: "Crédito", outro: "Outros" };
const TYPE_ICONS = { dinheiro: Banknote, pix: QrCode, debito: CreditCard, credito: CreditCard, outro: CreditCard };
const STEPS = ["Aluno", "Itens", "Pagamento", "Confirmar"];

export default function NovaVendaModal({ open, onOpenChange, initialStudent = null, onSaved }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [student, setStudent] = useState(null);
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [searching, setSearching] = useState(false);
  const [quickPhone, setQuickPhone] = useState("");
  const [tab, setTab] = useState("plans");
  const [itemQuery, setItemQuery] = useState("");
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [pms, setPms] = useState([]);
  const [cart, setCart] = useState([]);
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [payments, setPayments] = useState([]);
  const [payAmount, setPayAmount] = useState("");
  const [sellers, setSellers] = useState([]);
  const [sellerId, setSellerId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(initialStudent ? 1 : 0);
      setStudent(initialStudent || null);
      setSearch("");
      setStudents([]);
      setQuickPhone("");
      setTab("plans");
      setItemQuery("");
      setCart([]);
      setCustomName("");
      setCustomPrice("");
      setPayments([]);
      setPayAmount("");
      setSellerId("");
      api.get("/plans").then((r) => setPlans(r.data.filter((p) => p.active))).catch(() => {});
      api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active))).catch(() => {});
      api.get("/payment-methods").then((r) => setPms(r.data.filter((p) => p.active))).catch(() => {});
      api.get("/sellers").then((r) => setSellers(r.data.filter((s) => s.active))).catch(() => {});
    }
  }, [open, initialStudent]);

  useEffect(() => {
    if (!open || step !== 0 || !search.trim()) {
      setStudents([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/students", { params: { q: search } });
        setStudents(r.data);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [search, open, step]);

  const total = useMemo(() => cart.reduce((a, c) => a + c.unit_price * c.qty, 0), [cart]);

  const addToCart = (newItem) => {
    setCart((c) => {
      const idx = c.findIndex((x) => x.kind === newItem.kind && x.ref_id === newItem.ref_id);
      if (idx >= 0) {
        const copy = [...c];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...c, { ...newItem, qty: 1 }];
    });
  };

  const changeQty = (i, delta) =>
    setCart((c) => c.map((x, idx) => (idx === i ? { ...x, qty: Math.max(1, x.qty + delta) } : x)));

  const removeFromCart = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const selectCustom = () => {
    const price = parseFloat(String(customPrice).replace(",", "."));
    if (!customName.trim() || !price || price <= 0) {
      toast.error("Informe nome e valor do item avulso");
      return;
    }
    addToCart({ kind: "custom", ref_id: null, name: customName.trim(), unit_price: price });
    setCustomName("");
    setCustomPrice("");
    toast.success("Item adicionado à venda");
  };

  const submit = async () => {
    if (cart.length === 0 || saving) return;
    if (sellers.length > 0 && !sellerId) {
      toast.error("Selecione o vendedor responsável pela venda");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/sales", {
        student_id: student?.id || null,
        items: cart.map((c) => ({ kind: c.kind, ref_id: c.ref_id, name: c.name, qty: c.qty, unit_price: c.unit_price })),
        payments: payments.map((p) => ({ payment_method_id: p.payment_method_id, amount: p.amount })),
        allow_partial: remaining > 0.001,
        seller_id: sellerId || null,
        seller_name: sellers.find((s) => s.id === sellerId)?.name || null,
      });
      toast.success(`Venda registrada — ${brl(r.data.sale.total)}`, {
        description: r.data.sale.pending_amount > 0
          ? `Pendente: ${brl(r.data.sale.pending_amount)} a receber`
          : r.data.sale.overpay > 0
            ? `Ficou no caixa: ${brl(r.data.sale.overpay)}`
            : r.data.sale.student_name || "Venda avulsa",
      });
      (r.data.low_stock || []).forEach((p) =>
        toast.warning(`Estoque baixo: ${p.name}`, { description: `Restam ${p.stock} unidades` })
      );
      qc.invalidateQueries();
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const msg = apiError(e);
      if (msg.includes("Caixa fechado")) {
        toast.error("Caixa fechado", {
          description: "Abra o caixa antes de registrar vendas.",
          action: { label: "Abrir caixa", onClick: () => navigate("/caixa") },
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open || step !== 3) return;
    const h = (e) => {
      if (e.key === "Enter") submit();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const pmGroups = useMemo(() => {
    const g = {};
    pms.forEach((p) => {
      (g[p.type] = g[p.type] || []).push(p);
    });
    return g;
  }, [pms]);

  const norm = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const iq = norm(itemQuery.trim());
  const filteredPlans = plans.filter((p) => norm(p.name).includes(iq));
  const filteredProducts = products.filter((p) => norm(p.name).includes(iq));

  const paidTotal = useMemo(() => payments.reduce((a, p) => a + p.amount, 0), [payments]);
  const remaining = Math.max(0, Math.round((total - paidTotal) * 100) / 100);

  const addPayment = (pm) => {
    const value = payAmount ? parseFloat(String(payAmount).replace(",", ".")) : remaining;
    if (!value || value <= 0) {
      toast.error("Informe o valor pago");
      return;
    }
    const next = [...payments, { payment_method_id: pm.id, name: pm.name, type: pm.type, amount: Math.round(value * 100) / 100 }];
    setPayments(next);
    setPayAmount("");
    const newRemaining = Math.round((total - next.reduce((a, p) => a + p.amount, 0)) * 100) / 100;
    if (newRemaining <= 0.001) setStep(3);
  };

  const removePayment = (i) => setPayments((p) => p.filter((_, idx) => idx !== i));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0" data-testid="nova-venda-modal">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-display text-lg font-semibold">Nova Venda</DialogTitle>
            <div className="flex items-center gap-1.5">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-1.5">
                  <div
                    className={cn(
                      "h-1.5 rounded-full transition-[width,background-color] duration-300",
                      i <= step ? "w-8 bg-emerald-500" : "w-4 bg-muted"
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 min-h-[340px]">
          {step === 0 && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  autoFocus
                  data-testid="nova-venda-student-search"
                  placeholder="Buscar aluno por nome, telefone ou CPF..."
                  className="pl-9 h-12 text-base"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="max-h-52 overflow-y-auto space-y-1">
                {students.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`nova-venda-student-${s.id}`}
                    onClick={() => {
                      setStudent({ id: s.id, name: s.name });
                      setStep(1);
                    }}
                    className="w-full flex items-center justify-between rounded-lg border border-border px-4 py-3 text-left transition-colors duration-150 hover:bg-accent"
                  >
                    <div>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.phone || "Sem telefone"}</div>
                    </div>
                    {s.plan_name && (
                      <span className="text-xs text-muted-foreground">
                        {s.plan_name} · vence {s.due_date ? s.due_date.split("-").reverse().join("/") : "—"}
                      </span>
                    )}
                  </button>
                ))}
                {searching && <div className="py-4 text-center text-sm text-muted-foreground">Buscando...</div>}
              </div>
              {search.trim() && !searching && students.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-4 space-y-3">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <UserPlus className="h-4 w-4" /> Cadastrar "{search}" rapidamente
                  </div>
                  <div className="flex gap-2">
                    <Input
                      data-testid="nova-venda-quick-phone"
                      placeholder="Telefone (opcional)"
                      value={quickPhone}
                      onChange={(e) => setQuickPhone(e.target.value)}
                    />
                    <Button
                      data-testid="nova-venda-quick-create"
                      onClick={() => {
                        setStudent({ id: null, name: search.trim(), isNew: true, phone: quickPhone });
                        setStep(1);
                      }}
                    >
                      Cadastrar
                    </Button>
                  </div>
                </div>
              )}
              <button
                data-testid="nova-venda-skip-student"
                onClick={() => {
                  setStudent(null);
                  setStep(1);
                }}
                className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <UserX className="h-4 w-4" /> Continuar sem aluno (venda avulsa)
              </button>
            </div>
          )}

          {step === 1 && (
            <>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="plans" data-testid="nova-venda-tab-plans">Planos</TabsTrigger>
                <TabsTrigger value="products" data-testid="nova-venda-tab-products">Produtos</TabsTrigger>
                <TabsTrigger value="custom" data-testid="nova-venda-tab-custom">Outro</TabsTrigger>
              </TabsList>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="nova-venda-item-search"
                  placeholder="Buscar plano ou produto..."
                  className="pl-9"
                  value={itemQuery}
                  onChange={(e) => setItemQuery(e.target.value)}
                />
              </div>
              <TabsContent value="plans" className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {filteredPlans.map((p) => {
                  const inCartQty = cart.find((c) => c.kind === "plan" && c.ref_id === p.id)?.qty || 0;
                  const inCart = inCartQty > 0;
                  return (
                    <button
                      key={p.id}
                      data-testid={`nova-venda-plan-${p.id}`}
                      onClick={() => addToCart({ kind: "plan", ref_id: p.id, name: p.name, unit_price: p.price })}
                      className={cn(
                        "rounded-lg border p-4 text-left transition-all duration-150 hover:border-emerald-500/50 hover:bg-accent",
                        inCart ? "border-emerald-500 bg-emerald-500/10" : "border-border"
                      )}
                    >
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="num mt-1 text-lg font-semibold text-emerald-500">{brl(p.price)}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.days} dias{inCart && ` · ${inCartQty}x na venda`}
                      </div>
                    </button>
                  );
                })}
              </TabsContent>
              <TabsContent value="products" className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                {filteredProducts.map((p) => {
                  const out = p.track_stock && p.stock <= 0;
                  const inCartQty = cart.find((c) => c.kind === "product" && c.ref_id === p.id)?.qty || 0;
                  const maxed = p.track_stock && inCartQty >= p.stock && !out;
                  return (
                    <button
                      key={p.id}
                      data-testid={`nova-venda-product-${p.id}`}
                      disabled={out || maxed}
                      onClick={() => addToCart({ kind: "product", ref_id: p.id, name: p.name, unit_price: p.price })}
                      className={cn(
                        "rounded-lg border p-4 text-left transition-all duration-150 hover:border-emerald-500/50 hover:bg-accent disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-transparent",
                        inCartQty > 0 ? "border-emerald-500 bg-emerald-500/10" : "border-border"
                      )}
                    >
                      <div className="font-medium text-sm leading-tight">{p.name}</div>
                      <div className="num mt-1 text-lg font-semibold text-emerald-500">{brl(p.price)}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.track_stock ? (out ? "Sem estoque" : `${p.stock} em estoque`) : "Serviço"}
                        {inCartQty > 0 && ` · ${inCartQty}x na venda`}
                      </div>
                    </button>
                  );
                })}
              </TabsContent>
              <TabsContent value="custom" className="mt-4 space-y-3">
                <Input
                  data-testid="nova-venda-custom-name"
                  placeholder="Descrição (ex: Avaliação, Aluguel de toalha)"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
                <div className="flex gap-2">
                  <Input
                    data-testid="nova-venda-custom-price"
                    placeholder="Valor (ex: 25,00)"
                    inputMode="decimal"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                  />
                  <Button data-testid="nova-venda-custom-add" onClick={selectCustom}>
                    Adicionar
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
            {cart.length > 0 && (
              <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid="nova-venda-cart">
                {cart.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm" data-testid={`cart-item-${i}`}>
                    <span className="truncate font-medium">{c.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {(c.kind === "product" || c.kind === "plan") && (
                        <>
                          <Button variant="outline" size="icon" className="h-6 w-6" data-testid={`cart-minus-${i}`} onClick={() => changeQty(i, -1)}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="num w-5 text-center text-xs font-semibold">{c.qty}</span>
                          <Button variant="outline" size="icon" className="h-6 w-6" data-testid={`cart-plus-${i}`} onClick={() => changeQty(i, 1)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                      <span className="num w-20 text-right font-semibold">{brl(c.unit_price * c.qty)}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-500" data-testid={`cart-remove-${i}`} onClick={() => removeFromCart(i)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-emerald-500/20 pt-2">
                  <span className="num text-lg font-bold text-emerald-500" data-testid="cart-total">{brl(total)}</span>
                  <Button data-testid="nova-venda-continue" onClick={() => setStep(2)} className="bg-emerald-600 text-white hover:bg-emerald-500 transition-colors">
                    Continuar ({cart.length} {cart.length === 1 ? "item" : "itens"})
                  </Button>
                </div>
              </div>
            )}
            </>
          )}

          {step === 2 && cart.length > 0 && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted/60 px-4 py-3 space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {cart.length} {cart.length === 1 ? "item" : "itens"} — {student?.name || "Venda avulsa"}
                  </span>
                  <span className="num font-semibold" data-testid="payment-total">Total: {brl(total)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Pago: <span className="num font-semibold text-emerald-500" data-testid="payment-paid">{brl(paidTotal)}</span>
                  </span>
                  <span
                    className={cn("num font-semibold", remaining > 0.001 ? "text-amber-500" : "text-emerald-500")}
                    data-testid="payment-remaining"
                  >
                    {remaining > 0.001 ? `Faltam ${brl(remaining)}` : "Pagamento completo"}
                  </span>
                </div>
              </div>

              {payments.length > 0 && (
                <div className="space-y-1.5">
                  {payments.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm"
                      data-testid={`payment-part-${i}`}
                    >
                      <span>{p.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="num font-semibold text-emerald-500">{brl(p.amount)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-red-500"
                          data-testid={`payment-remove-${i}`}
                          onClick={() => removePayment(i)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {remaining > 0.001 && (
                <>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                    <Input
                      data-testid="payment-amount"
                      inputMode="decimal"
                      placeholder={`Valor (vazio = ${brl(remaining)})`}
                      className="pl-9 num"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                    />
                  </div>
                  <div className="space-y-4 max-h-56 overflow-y-auto">
                    {Object.entries(pmGroups).map(([type, list]) => {
                      const Icon = TYPE_ICONS[type] || CreditCard;
                      return (
                        <div key={type}>
                          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                            <Icon className="h-3.5 w-3.5" /> {TYPE_LABELS[type] || type}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {list.map((pm) => (
                              <button
                                key={pm.id}
                                data-testid={`nova-venda-pm-${pm.id}`}
                                onClick={() => addPayment(pm)}
                                className="rounded-lg border border-border px-4 py-3 text-sm font-medium transition-all duration-150 hover:border-emerald-500/50 hover:bg-accent"
                              >
                                {pm.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
                    data-testid="finalize-pending-btn"
                    onClick={() => setStep(3)}
                  >
                    Finalizar com pendência ({brl(remaining)} a receber)
                  </Button>
                </>
              )}
            </div>
          )}

          {step === 3 && cart.length > 0 && (
            <div className="space-y-5">
              <div className="rounded-xl border border-border divide-y divide-border max-h-56 overflow-y-auto">
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-muted-foreground">Aluno</span>
                  <span className="font-medium">{student?.name || "Venda avulsa"}</span>
                </div>
                {cart.map((c, i) => (
                  <div key={i} className="flex justify-between px-4 py-3 text-sm" data-testid={`confirm-item-${i}`}>
                    <span className="text-muted-foreground">
                      {c.kind === "plan" ? "Plano" : c.kind === "product" ? "Produto" : "Item"}
                    </span>
                    <span className="font-medium">
                      {c.qty > 1 ? `${c.qty}x ` : ""}
                      {c.name}
                      <span className="num ml-2 text-muted-foreground">{brl(c.unit_price * c.qty)}</span>
                    </span>
                  </div>
                ))}
                {payments.map((p, i) => (
                  <div key={i} className="flex justify-between px-4 py-3 text-sm" data-testid={`confirm-payment-${i}`}>
                    <span className="text-muted-foreground">{i === 0 ? "Pagamento" : ""}</span>
                    <span className="font-medium">
                      {p.name}
                      <span className="num ml-2 text-muted-foreground">{brl(p.amount)}</span>
                    </span>
                  </div>
                ))}
                {remaining > 0.001 && (
                  <div className="flex justify-between px-4 py-3 text-sm" data-testid="confirm-pending">
                    <span className="text-muted-foreground">Restante a receber</span>
                    <span className="num font-semibold text-amber-500">{brl(remaining)}</span>
                  </div>
                )}
                {paidTotal > total + 0.001 && (
                  <div className="flex justify-between px-4 py-3 text-sm" data-testid="confirm-overpay">
                    <span className="text-muted-foreground">Fica no caixa (troco não devolvido)</span>
                    <span className="num font-semibold text-sky-500">{brl(paidTotal - total)}</span>
                  </div>
                )}
                <div className="flex justify-between px-4 py-3 text-sm items-center">
                  <span className="text-muted-foreground">Vendedor <span className="text-red-500">*</span></span>
                  <Select value={sellerId} onValueChange={setSellerId}>
                    <SelectTrigger className="w-44 h-8" data-testid="nova-venda-seller">
                      <SelectValue placeholder="Selecionar vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {sellers.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`nova-venda-seller-${s.id}`}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-between px-4 py-4 items-center">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="num text-3xl font-bold text-emerald-500" data-testid="nova-venda-total">
                    {brl(total)}
                  </span>
                </div>
              </div>
              <Button
                data-testid="nova-venda-confirm"
                onClick={submit}
                disabled={saving}
                className="w-full h-12 text-base bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Confirmar Venda (Enter)
              </Button>
            </div>
          )}
        </div>

        {step > 0 && step < 3 && (
          <div className="px-6 pb-5">
            <Button variant="ghost" size="sm" data-testid="nova-venda-back" onClick={() => setStep((s) => s - 1)}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
            </Button>
          </div>
        )}
        {step === 3 && (
          <div className="px-6 pb-5">
            <Button variant="ghost" size="sm" data-testid="nova-venda-back-confirm" onClick={() => setStep(2)}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
